/**
 * Finding the link a composer should preview.
 *
 * One shared implementation, because the composer and the rendered post must
 * agree about which link gets a card. If they disagreed, the author would
 * compose against one preview and publish another.
 */

/**
 * Trailing characters that are almost always punctuation around a link rather
 * than part of it: "se her (https://dr.dk)." must not preview `dr.dk).`.
 * Closing brackets are only trimmed when unbalanced, so a Wikipedia URL
 * ending in `_(film)` survives.
 */
function trimTrailingPunctuation(url: string): string {
  let out = url;
  for (;;) {
    const last = out[out.length - 1];
    if (last === undefined) break;
    if (".,;:!?'\"".includes(last)) {
      out = out.slice(0, -1);
      continue;
    }
    if (last === ")" && (out.match(/\(/g)?.length ?? 0) < (out.match(/\)/g)?.length ?? 0)) {
      out = out.slice(0, -1);
      continue;
    }
    if (last === "]" && (out.match(/\[/g)?.length ?? 0) < (out.match(/\]/g)?.length ?? 0)) {
      out = out.slice(0, -1);
      continue;
    }
    break;
  }
  return out;
}

/**
 * The first link in some composer text, or null.
 *
 * ONLY THE FIRST. A post with five links gets one card, the way every social
 * product does it — five stacked previews is not a richer post, it is an
 * unreadable one.
 *
 * Markdown image syntax is skipped: `![alt](https://…)` is an attachment the
 * author already sees inline, and previewing it would show the same picture
 * twice. A normal markdown link `[text](https://…)` DOES preview — the author
 * chose to link something.
 */
export function findFirstUrl(text: string): string | null {
  if (!text) return null;
  const withoutImages = text.replace(/!\[[^\]]*\]\([^)]*\)/g, " ");
  const withoutCode = withoutImages.replace(/`[^`]*`/g, " ").replace(/```[\s\S]*?```/g, " ");
  const match = withoutCode.match(/https?:\/\/[^\s<>"'`]+/);
  if (!match) return null;
  const url = trimTrailingPunctuation(match[0]);
  // A bare scheme is not a link anybody meant to share.
  return /^https?:\/\/[^/\s.]+\.[^/\s]/.test(url) ? url : null;
}

/** Every link in the text, first occurrence order, de-duplicated. */
export function findAllUrls(text: string): string[] {
  if (!text) return [];
  const withoutImages = text.replace(/!\[[^\]]*\]\([^)]*\)/g, " ");
  const withoutCode = withoutImages.replace(/`[^`]*`/g, " ").replace(/```[\s\S]*?```/g, " ");
  const found = withoutCode.match(/https?:\/\/[^\s<>"'`]+/g) ?? [];
  const out: string[] = [];
  for (const raw of found) {
    const url = trimTrailingPunctuation(raw);
    if (/^https?:\/\/[^/\s.]+\.[^/\s]/.test(url) && !out.includes(url)) out.push(url);
  }
  return out;
}

/**
 * Does this URL point at a Theta group, post or event?
 *
 * A HINT, NOT A DECISION. It only decides which endpoint the client asks; the
 * server resolves the target under the viewer's own visibility and answers 404
 * when they may not see it. Nothing here is allowed to conclude that a preview
 * is safe to render.
 *
 * Kept in step with `parseInternalTarget` in
 * `backend/src/routes/v1/link-previews.ts` — the same three path shapes.
 */
export function isInternalThetaUrl(url: string): boolean {
  let path: string;
  try {
    path = url.startsWith("/") ? url : new URL(url).pathname;
  } catch {
    return false;
  }
  const seg = path.split("?")[0]!.split("#")[0]!.split("/").filter(Boolean);
  if (seg[0] === "grupper" && seg[2] === "opslag" && seg[3]) return true;
  if (seg[0] === "grupper" && seg[1] && seg.length === 2) return true;
  if (seg[0] === "opslag" && seg[1]) return true;
  if (seg[0] === "begivenheder" && seg[1]) return true;
  return false;
}

/**
 * Are these two spellings of a link the same target?
 *
 * THE COMPOSER AND THE PUBLISHED POST DO NOT SPELL A URL THE SAME WAY, and a
 * dismissal has to travel between them (OSO-568). The composer holds what the
 * author typed; the body holds what the renderer wrote into an `href` and then
 * escaped — so `https://dr.dk/?a=1&b=2` comes back as
 * `https://dr.dk/?a=1&amp;b=2`, and `https://dr.dk/æbler` as
 * `https://dr.dk/%C3%A6bler`. Comparing the raw strings would let a dismissed
 * card return for exactly the links people actually share.
 *
 * Two steps, in this order: undo the HTML escaping, because an attribute VALUE
 * is the decoded text and `&amp;` in the markup is one `&` in the url; then let
 * `URL` settle percent-encoding, host case and the empty path. A string the
 * parser refuses falls back to its decoded self, so a malformed pair is still
 * equal to itself.
 */
export function sameLinkTarget(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  return !!a && !!b && canonicalLink(a) === canonicalLink(b);
}

const HTML_ENTITIES: Record<string, string> = {
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#39;": "'",
};

function canonicalLink(url: string): string {
  const decoded = url.replace(
    /&(?:amp|lt|gt|quot|#39);/g,
    (entity) => HTML_ENTITIES[entity] ?? entity,
  );
  try {
    return new URL(decoded).href;
  } catch {
    return decoded;
  }
}

/** `www.dr.dk` from a URL, for the card's domain line. */
export function domainOf(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

/**
 * The first link in already-rendered post HTML.
 *
 * Reads `href`s rather than running the text matcher over the markup, so an
 * image `src`, a mention's profile href or a hashtag cannot be mistaken for a
 * shared link. Only absolute http(s) anchors count; an in-app relative link is
 * navigation, not something the author is sharing.
 */
export function findFirstLinkInHtml(html: string): string | null {
  if (!html) return null;
  const re = /<a\b[^>]*\bhref=["'](https?:\/\/[^"']+)["'][^>]*>/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html)) !== null) {
    const href = m[1]!;
    // Skip the anchors the renderer generates for its own affordances.
    if (/\bclass=["'][^"']*\b(mention|hashtag)\b/i.test(m[0])) continue;
    return href;
  }
  return null;
}
